/**
 * src/pages/Clientes.jsx
 * Lista de clientes con formulario de creación.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import { Input, Select } from '../components/ui/Input'
import { useConvenios } from '../hooks/useConvenios'
import { useClientes, useCreateCliente } from '../hooks/useClientes'

const TIER_OPTS = [
  { value: 'basic', label: 'Basic' },
  { value: 'pro', label: 'Pro' },
  { value: 'premium', label: 'Premium' },
]

const FREQ_OPTS = [
  { value: '24h', label: 'Cada 24h' },
  { value: '48h', label: 'Cada 48h' },
  { value: 'semanal', label: 'Semanal' },
  { value: 'manual', label: 'Manual' },
]

// Las tiendas soportadas las sirve el backend (app/convenios.py). La llamada
// vive en hooks/useConvenios.js: aquí solo se adapta a opciones de <select>.
function useOpcionesDeConvenio() {
  const convenios = useConvenios()
  return convenios.map(c => ({ value: c.slug, label: c.nombre, url: c.url_base }))
}

function CreateModal({ onClose }) {
  const familiaOpts = useOpcionesDeConvenio()
  const createMutation = useCreateCliente()
  const [form, setForm] = useState({
    cliente_id: '', nombre: '', email: '',
    tier: 'basic', familia_cm: 'alimentos2',
    url_base: 'https://conveniomarco2.mercadopublico.cl/alimentos2',
    empresa_rut: '', empresa_nombre: '', empresa_seller_code: '',
  })
  const [error, setError] = useState('')

  function set(field) {
    return e => setForm(f => ({ ...f, [field]: e.target.value }))
  }

  async function handleSubmit() {
    setError('')
    if (!form.cliente_id || !form.nombre || !form.empresa_rut || !form.empresa_seller_code) {
      setError('Completa los campos requeridos')
      return
    }
    try {
      await createMutation.mutateAsync({
        cliente_id: form.cliente_id.toLowerCase().replace(/\s+/g, '_'),
        nombre: form.nombre,
        email: form.email || undefined,
        tier: form.tier,
        familia_cm: form.familia_cm,
        url_base: form.url_base,
        empresas_propias: [{
          rut: form.empresa_rut,
          nombre: form.empresa_nombre || form.nombre,
          seller_code: form.empresa_seller_code,
        }],
        competidores: [],
      })
      onClose()
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al crear el cliente')
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100, padding: '20px',
    }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: 'var(--color-surface)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: '520px',
        boxShadow: 'var(--shadow-lg)', animation: 'fadeIn 0.15s ease-out',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--color-text)' }}>
            Nuevo cliente
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-3)', fontSize: '20px', lineHeight: 1, padding: 'var(--space-05)',
          }}>×</button>
        </div>

        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
            <Input label="ID CLIENTE *" value={form.cliente_id} onChange={set('cliente_id')}
              placeholder="ej. nutrihouse" />
            <Select label="TIER" value={form.tier} onChange={set('tier')} options={TIER_OPTS} />
          </div>
          <Input label="NOMBRE *" value={form.nombre} onChange={set('nombre')}
            placeholder="Nombre de la empresa" />
          <Input label="EMAIL" value={form.email} onChange={set('email')}
            type="email" placeholder="contacto@empresa.cl" />
          <Select label="FAMILIA CM" value={form.familia_cm}
            onChange={e => {
              const fam = familiaOpts.find(x => x.value === e.target.value)
              setForm(f => ({ ...f, familia_cm: fam.value, url_base: fam.url }))
            }}
            options={familiaOpts} />

          <div style={{
            padding: '14px', background: 'var(--color-surface-2)',
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
          }}>
            <div style={{
              fontSize: 'var(--text-xs)', fontWeight: 500, color: 'var(--color-text-3)',
              fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: 'var(--space-3)',
            }}>
              EMPRESA PROPIA
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <Input label="RUT *" value={form.empresa_rut} onChange={set('empresa_rut')}
                placeholder="76.000.000-0" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <Input label="NOMBRE" value={form.empresa_nombre} onChange={set('empresa_nombre')}
                  placeholder="Razón social" />
                <Input label="SELLER CODE *" value={form.empresa_seller_code} onChange={set('empresa_seller_code')}
                  placeholder="1234567" />
              </div>
            </div>
          </div>

          {error && (
            <div style={{
              background: 'var(--color-error-soft)', border: '1px solid var(--color-error-line)',
              borderRadius: 'var(--radius-sm)', padding: '10px 12px',
              fontSize: 'var(--text-sm)', color: 'var(--color-error)', fontFamily: 'var(--font-mono)',
            }}>
              {error}
            </div>
          )}
        </div>

        <div style={{
          padding: '14px 24px', borderTop: '1px solid var(--color-border)',
          display: 'flex', gap: '10px', justifyContent: 'flex-end',
        }}>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            loading={createMutation.isPending}
          >
            Crear cliente
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ───────────────────────────────────────────────────────────────────────────
   La tabla, agrupada por PREGUNTA (Manuel, 05-09-2026, opción A del mockup).

   Venía de diez columnas que no cabían: 460 px de más en el ancho útil del panel
   (~1.160 px a 1440), así que para ver si un cliente estaba en prueba había que
   arrastrar — y al arrastrar se perdía de vista de quién era la fila.

   No se ha quitado ni un dato. Se han fundido las columnas que contestaban a la
   misma pregunta:

     ID + Nombre        → «Cliente»  · son el mismo dato dicho dos veces
     Plan + Estado      → «Plan»     · nunca se leen por separado
     Diagnóstico +
     Contratación +
     Usuarios           → «Ciclo»    · tres fechas de la MISMA historia, y en
                                       orden se leen mejor que en tres sitios

   ⚠️ Lo que esto cuesta, y por eso existe `ORDENES` más abajo: una columna
   agrupada ya no se puede ordenar con un clic en su cabecera. Sin reponer ese
   orden en algún sitio, agrupar no sería un rediseño: sería quitar una función.
   ─────────────────────────────────────────────────────────────────────────── */
const COLUMNS = [
  {
    // Nombre e ID juntos: el ID es la clave técnica (llega a 40 caracteres en
    // `comercializadora_emporio_del_abuelo_luci`, que él solo se llevaba una
    // cuarta parte de la tabla) y el nombre es cómo se le llama. Uno debajo del
    // otro se leen igual y ocupan una columna en vez de dos.
    key: 'nombre', label: 'Cliente', width: '280px', sortable: true,
    render: (v, row) => (
      <span style={{ display: 'inline-flex', flexDirection: 'column' }}>
        <span>{v}</span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)',
                       color: 'var(--color-text-3)', wordBreak: 'break-all' }}>
          {row.cliente_id}
        </span>
      </span>
    ),
  },
  {
    // TODOS los convenios del tenant, no solo el del alta (Manuel, 05-09): un
    // multitienda —Manquehue: ferretería + aseo— salía aquí como si operara en
    // uno solo. El backend manda la lista ya resuelta ({slug, nombre}); componerla
    // aquí desde `familia_cm` sería una segunda fuente para el mismo texto.
    key: 'convenio', label: 'Convenio', mono: true, width: '160px', sortable: true,
    render: (v, row) => {
      const cs = row.convenios || []
      // Fallback al singular: un backend antiguo (o un cliente en caché) no manda
      // la lista, y dejar la celda vacía sería perder un dato que sí se tiene.
      if (!cs.length) return v || <span style={{ color: 'var(--color-text-3)' }}>—</span>
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column',
                       gap: 'var(--space-05)' }}>
          {cs.map(c => (
            <span key={c.slug} title={c.slug}>{c.nombre}</span>
          ))}
        </span>
      )
    },
  },
  {
    // Plan y estado en la misma celda: «qué paga» y «sigue con nosotros» se leen
    // siempre juntos, y separados costaban dos columnas para una sola pregunta.
    // Una baja PROGRAMADA se dice con su fecha: el cliente sigue activo hasta
    // entonces, y pintarlo solo como «activo» esconde que se va.
    key: 'tier', label: 'Plan', width: '120px', sortable: true,
    render: (v, row) => (
      <span style={{ display: 'inline-flex', flexDirection: 'column',
                     alignItems: 'flex-start', gap: 'var(--space-05)' }}>
        <Badge color={TIER_COLOR[v] || 'gray'}>{v}</Badge>
        {/* Un pago pendiente manda sobre todo lo demás en esta celda: es lo
            único que exige una llamada hoy. Antes caía en el cubo «otros», que
            se rotula «manual / sin plan», así que un cliente que dejó de pagar
            se veía como un alta manual. */}
        {/* Ya pagó, su periodo venció y Flow no ha conseguido renovarlo. NO se le
            corta solo (opción B, Manuel 07-09): esto dice a quién mirar, y el
            corte se decide en «Zona de riesgo» de su ficha. Antes se contaba como
            «de pago», o sea que la tabla afirmaba que estaba al corriente. */}
        {row.suscripcion === 'renovacion_vencida' && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warn)',
                         fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}
                title="Su periodo pagado venció y la renovación no se ha cobrado. No se le ha cortado el servicio: decídelo en «Zona de riesgo» de su ficha.">
            renovación vencida
          </span>
        )}
        {row.suscripcion === 'impago' && (
          <Badge color="red" dot>pago pendiente</Badge>
        )}
        {/* ⚠️ NO es lo mismo, y hasta el 07-09 se llamaban igual. Manuel, sobre
            partesypiezas: «¿hizo también una suscripción de pago? porque no me
            consta que así fuese». No la hizo — a `past_due` se llega también al
            acabarse la prueba, cuando se emite el primer cobro—. A este se le
            cierra una primera venta; al de arriba se le rescata. */}
        {row.suscripcion === 'prueba_impagada' && (
          <Badge color="red" dot>prueba sin pagar</Badge>
        )}
        {/* La prueba AGOTADA. Al fusionar las columnas (opción A, 07-09) este
            caso se quedó sin sitio: «Prueba en curso» solo enseña las vigentes
            —es lo que promete su título— y aquí no había distintivo de trial,
            así que un tenant con el trial caducado se leía igual que uno al día.
            Es justo el que hay que ver: sigue usando la herramienta y el
            mantenimiento no le ha cerrado el ciclo.
            ⚠️ Texto suelto y no un Badge, y MEDIDO en el navegador (1280 px):
            `pago pendiente` con su cápsula ocupa 125 px que la celda no puede
            bajar (`nowrap`); este renglón se queda en 92. Con cápsula pasaría a
            ser el nuevo mínimo de la columna y se comería el margen que hace que
            la tabla quepa sin barra horizontal. */}
        {row.comercial?.prueba_vencida && (
          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)',
                         fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}
                title={`Prueba gratuita agotada el ${_fechaCorta(row.comercial.prueba_vencida.hasta)}`
                       + ` (hace ${row.comercial.prueba_vencida.dias_vencida} d).`
                       + ' La suscripcion sigue abierta en «trialing»: el mantenimiento no ha podido cerrarla.'}>
            prueba vencida
          </span>
        )}
        {row.activo === false
          ? <Badge color="gray" dot>inactivo</Badge>
          : row.baja_programada_en
            ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-warn)',
                             fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' }}
                    title="Baja del servicio programada">
                baja {_fechaCorta(row.baja_programada_en)}
              </span>
            : null}
      </span>
    ),
  },
  {
    // Dónde se han ejecutado el censo y la sonda de convenios, y dónde no
    // (Manuel, 29-08). El backend decide el estado; aquí solo se elige el color,
    // para que no haya dos criterios.
    key: 'convenios_cm', label: 'Convenios CM', width: '150px',
    render: (v) => {
      if (!v) return <span style={{ color: 'var(--color-text-3)' }}>○ sin datos</span>
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center',
                       gap: 'var(--space-1)', flexWrap: 'wrap' }}
              title={v.detalle}>
          <Badge color={COLOR_CONVENIOS[v.estado] || 'gray'} dot>{v.etiqueta}</Badge>
          {/* Avisa, no acusa: un run posterior puede no haber traído ninguna
              orden nueva, así que esto no tapa el estado del censo. */}
          {v.hubo_run_despues && (
            <span title="Ha corrido el pipeline después del censo: puede haber órdenes nuevas sin censar"
                  style={{ color: 'var(--color-warn)', fontFamily: 'var(--font-mono)' }}>⟳</span>
          )}
        </span>
      )
    },
  },
  {
    // Las DOS pruebas en una sola columna (Manuel, 05-09): para él «trial» es
    // tanto los 14 días gratuitos como una concesión comercial. Son mecanismos
    // distintos —uno en la pasarela de pago, otro en la ficha— pero la pregunta
    // es la misma: ¿a este cliente se le acaba algo, y cuándo? Si tiene las dos,
    // se ven las dos: quedarse con una escondería la otra.
    key: 'comercial', label: 'Prueba en curso', width: '175px',
    render: (v) => {
      const vig = v?.vigencias || []
      if (!vig.length) return <span style={{ color: 'var(--color-text-3)' }}>—</span>
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 'var(--space-05)' }}>
          {/* ⚠️ SIN `nowrap` en el envoltorio, y medido: con él, «concesión ·
              sin vencimiento» marcaba un ancho mínimo de 181 px que la tabla no
              podía bajar, y era el mayor de los seis. Suelto, la etiqueta y los
              días caen a dos líneas cuando aprieta — que es exactamente lo que
              tiene que hacer una celda para que la tabla quepa. */}
          {vig.map((x, i) => (
            <span key={i} title={x.detalle}>
              <Badge color={x.tipo === 'prueba' ? 'blue' : 'amber'} dot>
                {x.tipo === 'prueba' ? '14 días' : 'concesión'}
              </Badge>
              <span style={{ marginLeft: 'var(--space-1)', fontSize: 'var(--text-xs)',
                             color: x.dias !== null && x.dias <= 3
                               ? 'var(--color-warn)' : 'var(--color-text-2)' }}>
                {/* Una concesión sin fecha no caduca sola NUNCA: decirlo, o se
                    regala un plan para siempre sin que nadie se entere. */}
                {x.dias === null ? 'sin vencimiento' : `${x.dias} d`}
              </span>
            </span>
          ))}
        </span>
      )
    },
  },
  {
    // Las tres fechas del embudo, en el orden en que ocurren. Iban en tres
    // columnas y se leían como tres datos sueltos; en fila se leen como lo que
    // son: por dónde va este cliente.
    key: 'comercial_ciclo', label: 'Ciclo comercial', width: '235px',
    render: (_v, row) => {
      const c = row.comercial || {}
      const contr = c.contratacion || {}
      const usu = c.usuarios || {}
      const cont = _CONTRATACION[contr.tipo] || _CONTRATACION.desconocido
      return (
        <span style={{ display: 'inline-flex', flexDirection: 'column',
                       gap: 'var(--space-05)', fontSize: 'var(--text-xs)' }}>
          {/* «no consta» y no una casilla vacía: que un cliente NO viniera de un
              diagnóstico es un dato del embudo, no un hueco. */}
          <_Par k="diag" titulo="Fecha del diagnóstico que trajo a este cliente">
            {c.diagnostico?.fecha
              ? _fechaCorta(c.diagnostico.fecha)
              : <span style={{ color: 'var(--color-text-3)' }}>no consta</span>}
          </_Par>
          {/* ⚠️ Aquí NO se pinta «la fecha que haya»: se pinta lo que el backend
              haya podido AFIRMAR. Sólo dos de los cinco casos son una fecha de
              contratación fiable; los demás llevan su matiz al lado o no son una
              fecha en absoluto. Ver el punto 4 de app/services/contratacion.py. */}
          <_Par k="contr" titulo={cont.titulo}>
            {cont.texto
              ? <span style={{ color: 'var(--color-text-2)' }}>{cont.texto}</span>
              : contr.fecha
                ? <span style={{ color: cont.tenue ? 'var(--color-text-3)' : 'inherit' }}>
                    {_fechaCorta(contr.fecha)}{cont.coletilla && ` ${cont.coletilla}`}
                  </span>
                : <span style={{ color: 'var(--color-text-3)' }}>—</span>}
          </_Par>
          <_Par k="usu" titulo="Usuarios del panel y fecha de la primera alta">
            {usu.n
              ? `${usu.n} · desde ${_fechaCorta(usu.primera_alta)}`
              : <span style={{ color: 'var(--color-text-3)' }}>0</span>}
          </_Par>
        </span>
      )
    },
  },
]

/* Los cinco desenlaces de «cuándo contrató», y cómo se dice cada uno.
 *
 * ⚠️ La tabla vive aquí y no repartida por el JSX porque el fallo que arregla
 * fue exactamente ese: había UN caso etiquetado (el alta técnica) y todo lo demás
 * caía en «pinta la fecha», sin preguntarse si esa fecha era una contratación.
 * Manuel lo vio el 07-09-2026 en Emporio del Abuelo: una fecha bajo CONTR para
 * alguien que solo tuvo una prueba vencida. Puestos en fila se ve lo que hay que
 * ver: solo `conversion` y `suscripcion` son una contratación afirmable.
 *
 * `texto` gana a la fecha: cuando no hay contratación, no se enseña ninguna. */
const _CONTRATACION = {
  // Pagó desde el primer día. La fecha es suya y no necesita matiz.
  suscripcion: { titulo: 'Inicio del periodo de la suscripción' },
  // Vino de una prueba y convirtió: el día exacto en que empezó a pagar.
  conversion: { titulo: 'Día en que la prueba gratuita pasó a pagar' },
  // Prueba en curso, vencida o perdida: no hay contrato que fechar.
  sin_contratar: {
    texto: 'sin contratar',
    titulo: 'Empezó una prueba gratuita y no consta que llegara a pagar: '
          + 'no hay contratación que fechar',
  },
  // Anterior al 06-09-2026: puede ser el inicio de la prueba o el del pago.
  sin_rastro: {
    tenue: true, coletilla: 'sin rastro',
    titulo: 'Anterior al 06-09-2026, cuando aún no se registraba si una '
          + 'suscripción venía de una prueba: esta fecha puede ser el inicio de '
          + 'la prueba y no el de la contratación',
  },
  // Venta asistida sin suscripción: la única fecha es el alta del tenant.
  alta_tecnica: {
    tenue: true, coletilla: 'alta téc.',
    titulo: 'No hay suscripción registrada: es la fecha de alta del tenant, no '
          + 'la de contratación',
  },
  desconocido: { titulo: 'Sin datos de contratación' },
}

// Una línea del ciclo: rótulo corto en mono y su valor. El rótulo es lo que
// permite fundir tres columnas en una sin que las fechas se vuelvan adivinanzas.
function _Par({ k, titulo, children }) {
  return (
    <span style={{ display: 'flex', gap: 'var(--space-2)' }} title={titulo}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)',
                     color: 'var(--color-text-3)', textTransform: 'uppercase',
                     letterSpacing: '0.04em', minWidth: '32px' }}>{k}</span>
      {/* ⚠️ Sin `nowrap`, también medido: «12-08-26 alta téc.» marcaba 119 px de
          ancho mínimo. Las fechas son `dd-mm-aa`, un solo token sin espacios, así
          que no se parten aunque se deje envolver; lo que cae a la línea de abajo
          cuando aprieta es la coletilla, que es lo que puede caer. */}
      <span style={{ fontFamily: 'var(--font-mono)' }}>{children}</span>
    </span>
  )
}

/* Los órdenes de la lista.
 *
 * ⚠️ Existe porque agrupar columnas quita el clic-en-la-cabecera de las que se
 * fundieron: sin esto, ordenar por fecha de contratación —que es justo lo que se
 * ordena de verdad en una lista comercial— habría dejado de ser posible. Las tres
 * primeras siguen teniendo cabecera (y el <select> las repite, para que haya un
 * solo sitio donde mirar «¿cómo está ordenado esto?»); las tres últimas solo
 * viven aquí.
 *
 * `valor` devuelve texto o número; `null` va SIEMPRE al final, en los dos
 * sentidos. Un cliente sin fecha de contratación no es «el más antiguo»: es uno
 * del que no se sabe, y colarlo arriba al ordenar ascendente sería una respuesta
 * inventada.
 */
const ORDENES = [
  { clave: 'nombre',    etiqueta: 'Nombre',              valor: r => r.nombre },
  { clave: 'convenio',  etiqueta: 'Convenio',            valor: r => r.convenio },
  { clave: 'tier',      etiqueta: 'Plan',                valor: r => r.tier },
  { clave: 'alta',      etiqueta: 'Alta del tenant',     valor: r => _ts(r.creado_en) },
  { clave: 'contrat',   etiqueta: 'Contratación',        valor: r => _ts(r.comercial?.contratacion?.fecha) },
  { clave: 'diag',      etiqueta: 'Diagnóstico',         valor: r => _ts(r.comercial?.diagnostico?.fecha) },
  { clave: 'prueba',    etiqueta: 'Fin de prueba',       valor: _diasDePrueba },
  { clave: 'usuarios',  etiqueta: 'Nº de usuarios',      valor: r => r.comercial?.usuarios?.n ?? null },
  // Sobrevive a la fusión de «Estado» dentro de «Plan»: con el filtro en «Ver
  // todos», poder subir los activos sigue haciendo falta.
  { clave: 'estado',    etiqueta: 'Estado',              valor: r => (r.activo === false ? 1 : 0) },
]
const ORDEN_POR_CLAVE = Object.fromEntries(ORDENES.map(o => [o.clave, o]))
const ORDEN_OPTS = ORDENES.map(o => ({ value: o.clave, label: `Ordenar por ${o.etiqueta}` }))

// Fecha corta: lo que importa en esta tabla es el día, no el minuto.
function _fechaCorta(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit' })
}

function _ts(iso) {
  if (!iso) return null
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? null : t
}

// Días que le quedan a la prueba más corta. Un cliente con dos vigencias se
// ordena por la que vence antes, que es la que obliga a llamarle.
function _diasDePrueba(row) {
  const vig = (row.comercial?.vigencias || []).map(v => v.dias).filter(d => d !== null && d !== undefined)
  return vig.length ? Math.min(...vig) : null
}

const COLOR_CONVENIOS = {
  sin_censar: 'gray',
  a_medias: 'yellow',
  censado: 'blue',
  censado_y_sondeado: 'green',
  // Sin convenios sin mapear: no es un paso a medias, es que no hay trabajo.
  nada_que_sondear: 'green',
}

const TIER_FILTER_OPTS = [
  { value: '', label: 'Todos los tiers' },
  ...TIER_OPTS,
]

export default function Clientes() {
  const navigate = useNavigate()
  const [showCreate, setShowCreate] = useState(false)
  const [soloActivos, setSoloActivos] = useState(true)
  const [busqueda, setBusqueda] = useState('')
  const [tierFiltro, setTierFiltro] = useState('')
  const [familiaFiltro, setFamiliaFiltro] = useState('')
  const [sortKey, setSortKey] = useState('nombre')
  const [sortDir, setSortDir] = useState('asc')
  const { data: clientes = [], isLoading } = useClientes(soloActivos)

  // El desplegable ofrece TODAS las tiendas de TODOS los tenants, no solo las de
  // su alta: si la columna enseña que Manquehue está en aseo3, filtrar por aseo3
  // y no verlo sería la pantalla contradiciéndose a sí misma.
  const familias = [...new Set(clientes.flatMap(
    c => (c.convenios || []).map(x => x.slug).concat(c.familia_cm)))].sort()

  const q = busqueda.trim().toLowerCase()
  const filtrados = clientes
    .filter(c => !q
      || c.nombre.toLowerCase().includes(q)
      || c.cliente_id.toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q))
    .filter(c => !tierFiltro || c.tier === tierFiltro)
    // Casa por CUALQUIERA de sus tiendas, no solo por la del alta.
    .filter(c => !familiaFiltro
      || c.familia_cm === familiaFiltro
      || (c.convenios || []).some(x => x.slug === familiaFiltro))
    .sort((a, b) => {
      const orden = ORDEN_POR_CLAVE[sortKey] || ORDEN_POR_CLAVE.nombre
      const va = orden.valor(a), vb = orden.valor(b)
      // Los vacíos SIEMPRE al final, en los dos sentidos. Un cliente sin fecha de
      // contratación no es «el más antiguo»: es uno del que no se sabe, y ponerlo
      // arriba al ordenar ascendente sería una respuesta inventada.
      const na = va === null || va === undefined || va === ''
      const nb = vb === null || vb === undefined || vb === ''
      if (na || nb) return na && nb ? 0 : (na ? 1 : -1)
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), 'es')
      return sortDir === 'asc' ? cmp : -cmp
    })

  // Desde la cabecera, repetir la columna activa invierte el sentido; desde el
  // <select> no, porque elegir en un desplegable el valor que ya estaba puesto no
  // es pedir nada — y darle la vuelta a la lista sin haberlo pedido desconcierta.
  function handleSort(key, modo) {
    if (key === sortKey && modo !== 'fijar') setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else if (key !== sortKey) { setSortKey(key); setSortDir('asc') }
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Clientes"
        subtitle={`${filtrados.length} de ${clientes.length} clientes${soloActivos ? ' activos' : ''}`}
        action={
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <button
              onClick={() => setSoloActivos(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 'var(--text-sm)', color: 'var(--color-text-3)',
                fontFamily: 'var(--font-mono)', padding: '6px 10px',
              }}
            >
              {soloActivos ? 'Ver todos' : 'Solo activos'}
            </button>
            <Button
              size="sm"
              icon={<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>}
              onClick={() => setShowCreate(true)}
            >
              Nuevo cliente
            </Button>
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 220px', maxWidth: '340px' }}>
            <Input
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              placeholder="Buscar por nombre, ID o email…"
            />
          </div>
          <Select value={tierFiltro} options={TIER_FILTER_OPTS}
                  onChange={e => setTierFiltro(e.target.value)} />
          <Select value={familiaFiltro}
                  options={[{ value: '', label: 'Todas las familias' },
                            ...familias.map(f => ({ value: f, label: f }))]}
                  onChange={e => setFamiliaFiltro(e.target.value)} />
          {/* Repone el orden que las columnas agrupadas ya no pueden ofrecer con
              un clic en su cabecera. Las tres primeras opciones SÍ tienen cabecera:
              se repiten aquí a propósito, para que haya un solo sitio donde mirar
              «¿cómo está ordenado esto?» en vez de dos mecanismos a medias. */}
          <Select value={sortKey} options={ORDEN_OPTS}
                  onChange={e => handleSort(e.target.value, 'fijar')} />
          <button
            onClick={() => setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))}
            title={sortDir === 'asc' ? 'Ascendente — pulsa para invertir'
                                     : 'Descendente — pulsa para invertir'}
            style={{
              background: 'none', border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
              padding: '0 10px', color: 'var(--color-text-2)',
              fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)',
            }}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>

        <Card>
          <Table
            columns={COLUMNS}
            data={filtrados}
            loading={isLoading}
            emptyMessage={clientes.length ? 'Ningún cliente coincide con los filtros' : 'Sin clientes — crea el primero'}
            onRowClick={row => navigate(`/clientes/${row.cliente_id}`)}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
          />
        </Card>
      </div>

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} />}
    </div>
  )
}
